import { useTabList } from '@react-aria/tabs'
import { useTabListState } from '@react-stately/tabs'
import cx from 'classnames'

import { Tab } from './Tab'
import Styles from './Tabs.module.scss'
import { useObjectRef } from 'utils/useObjectRef'
import { AriaTabListProps, SpectrumTabsProps } from '@react-types/tabs'
import { useHeaderTheme } from 'hooks/useHeaderTheme'

interface TabsProps extends SpectrumTabsProps<HTMLDivElement> {
  /** Whether the transition between panels should be animated */
  animate?: boolean
  withEditorTopColor?: boolean
  classNames?: {
    /** Class name applied to the tabs list at the top */
    tabs?: string
    /** Class name applied to the tab items in the list at the top */
    tabItem?: string
    /** Class name applied to the tab */
    tab?: string
    /** Class name applied to the tabs container */
    tabsContainer?: string
    /** Class name applied to the tab content container */
    tabContentsContainer?: string
  }
}

export const Tabs = (props: TabsProps) => {
  const { animate, classNames = {}, withEditorTopColor = false } = props
  const state = useTabListState(props as AriaTabListProps<HTMLDivElement>)
  const ref = useObjectRef<HTMLDivElement>()
  const { tabListProps } = useTabList(props as AriaTabListProps<HTMLDivElement>, state, ref)
  const itemWithFraction = 100 / state.collection.size
  const translate = `-${itemWithFraction * (state.selectedItem?.index || 0)}%`
  const theme = useHeaderTheme()

  return (
    <>
      <div
        {...tabListProps}
        ref={ref}
        className={cx(Styles.tabsContainer, classNames.tabsContainer, {
          [Styles.withEditorTopColor]: withEditorTopColor,
        })}
        data-theme={withEditorTopColor ? theme : undefined}
      >
        <div className={cx(Styles.tabs, classNames.tabs)}>
          {[...state.collection].map((item) => (
            <Tab
              withEditorTopColor={withEditorTopColor}
              className={classNames.tabItem}
              key={item.key}
              item={item}
              state={state}
            />
          ))}
        </div>
      </div>
      <div className={cx(Styles.tabContentsContainer, classNames.tabContentsContainer, { [Styles.animate]: animate })}>
        <div
          style={{
            width: `${100 * state.collection.size}%`,
            transform: `translateX(${translate})`,
          }}
          className={Styles.tabsContainerInner}
        >
          {[...state.collection].map((tab) => {
            return (
              <div
                className={cx(Styles.tabContent, classNames.tab, { [Styles.withEditorTopColor]: withEditorTopColor })}
                style={{ width: `${itemWithFraction}%` }}
                key={tab.key}
              >
                {animate && tab.props.children}
                {!animate && state.selectedKey === tab.key && tab.props.children}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
